import React from 'react';
import {
  Star, MessageSquare, Loader2, CheckCircle, CreditCard, ExternalLink, Car
} from 'lucide-react';

export function OfferCard({ offer, transfer, t, acceptingOffer, onAccept }) {
  const [acceptedTerms, setAcceptedTerms] = React.useState(false);

  // Privacy: hide driver identity until the offer has been booked/paid.
  // Only show vehicle make/model, vehicle photo and aggregated rating.
  const isBooked = offer.status === 'accepted';
  const vehicleLabel = [offer.vehicle?.make, offer.vehicle?.model].filter(Boolean).join(' ');
  const vehiclePhoto = offer.vehicle?.photo || offer.vehicle?.image || offer.vehicle_photo;

  return (
    <div className={`border rounded-xl p-4 ${isBooked ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {/* Avatar / Vehicle thumb */}
          {isBooked ? (
            <a
              href={`/driver/public/${offer.driver_id}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="w-14 h-14 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-bold hover:ring-2 hover:ring-orange-300 transition-all overflow-hidden shrink-0"
              data-testid={`driver-avatar-${offer.id}`}
            >
              {offer.driver_picture ? (
                <img src={offer.driver_picture} alt="" className="w-full h-full object-cover" />
              ) : (
                offer.driver_name?.charAt(0)?.toUpperCase() || 'F'
              )}
            </a>
          ) : (
            <div
              className="w-16 h-16 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400 overflow-hidden shrink-0"
              data-testid={`vehicle-thumb-${offer.id}`}
            >
              {vehiclePhoto ? (
                <img src={vehiclePhoto} alt={vehicleLabel} className="w-full h-full object-cover" />
              ) : (
                <Car size={28} />
              )}
            </div>
          )}

          <div className="min-w-0 flex-1">
            {isBooked ? (
              <a
                href={`/driver/public/${offer.driver_id}`}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="font-medium text-slate-800 hover:text-orange-500 transition-colors flex items-center gap-1.5"
                data-testid={`driver-profile-link-${offer.id}`}
              >
                {offer.driver_name || t('dashboard_booked_driver')}
                <ExternalLink size={12} className="text-slate-400" />
              </a>
            ) : (
              // Privacy mode: no name, just a neutral label
              <p className="font-semibold text-slate-800" data-testid={`anonymous-driver-${offer.id}`}>
                {t('dashboard_offer_anonymous_driver') || 'Verifizierter Fahrer'}
              </p>
            )}
            <p className="text-sm text-slate-500" data-testid={`vehicle-label-${offer.id}`}>
              {vehicleLabel || 'Fahrzeug'}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <Star size={14} className="text-yellow-500 fill-yellow-500" />
              <span className="text-sm text-slate-600">{offer.driver_rating?.toFixed(1) || '5.0'}</span>
              <span className="text-xs text-slate-400">({offer.driver_trips || 0} {t('dashboard_trips')})</span>
            </div>
            {offer.notes && (
              <p className="text-xs text-slate-500 mt-2 flex items-start gap-1">
                <MessageSquare size={12} className="mt-0.5 shrink-0" />
                {offer.notes}
              </p>
            )}
            {!isBooked && (
              <p className="text-[11px] text-slate-400 mt-2 italic">
                {t('dashboard_offer_privacy_hint') || 'Fahrername & Kontakt werden nach Buchung freigegeben.'}
              </p>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-2xl font-bold text-slate-800">&euro;{offer.price}</p>
          {offer.status === 'pending' && (
            <div className="flex flex-col gap-1.5 mt-2 items-end">
              <label className="flex items-start gap-2 cursor-pointer max-w-[200px] mb-1" data-testid={`terms-checkbox-label-${offer.id}`}>
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => { e.stopPropagation(); setAcceptedTerms(e.target.checked); }}
                  className="mt-0.5 w-3.5 h-3.5 rounded border-slate-300 text-orange-500 focus:ring-orange-500 cursor-pointer"
                  data-testid={`terms-checkbox-${offer.id}`}
                />
                <span className="text-[10px] text-slate-500 leading-tight">
                  Ich akzeptiere die <a href="/agb" target="_blank" rel="noreferrer" className="text-orange-500 hover:underline" onClick={e => e.stopPropagation()}>AGB</a> & <a href="/datenschutz" target="_blank" rel="noreferrer" className="text-orange-500 hover:underline" onClick={e => e.stopPropagation()}>Datenschutz</a>
                </span>
              </label>
              <button
                onClick={(e) => { e.stopPropagation(); onAccept(transfer.id, offer.id, 'stripe'); }}
                disabled={acceptingOffer === offer.id || !acceptedTerms}
                className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm font-medium hover:bg-slate-900 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                data-testid={`pay-stripe-${offer.id}`}
              >
                {acceptingOffer === offer.id ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <>
                    <CreditCard size={14} />
                    Karte
                  </>
                )}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onAccept(transfer.id, offer.id, 'paypal'); }}
                disabled={acceptingOffer === offer.id || !acceptedTerms}
                className="px-4 py-2 bg-[#0070ba] text-white rounded-lg text-sm font-medium hover:bg-[#005ea6] transition-colors disabled:opacity-50 flex items-center gap-1.5"
                data-testid={`pay-paypal-${offer.id}`}
              >
                {acceptingOffer === offer.id ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797H9.207c-.665 0-1.084.498-1.175 1.049l-.953 6.108a.462.462 0 0 1-.003.02"/></svg>
                    PayPal
                  </>
                )}
              </button>
            </div>
          )}
          {isBooked && (
            <span className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-lg text-xs font-medium">
              <CheckCircle size={12} /> Bezahlt & Gebucht
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
